import React from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import withChartExport from '../../../components/ChartExporting';
import { formatWorkDuration } from '../../../util/dates';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { getBreakdownData } from '../CTBActions';
import { saveConfig, updateBreakdown, updateBreakdownType } from '../../../components/Filter/FilterActions';

import barIcon from '../../../assets/img/bar-icon.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import arrowDownIcon from '../../../assets/img/arrow_down.svg';
import { escapeHTML } from '../../../util/text';

const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [5, 5, 5, 5]
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    width: 230,
    layout: 'vertical',        
    align: 'left',            
    verticalAlign: 'top',
    itemMarginTop: 4,    
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: 'rgba(0, 0, 0, 0.87)',
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      innerSize: 0,
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
        useHTML: true,
        distance: 40,
        connectorWidth: 1,
        //alignTo: 'plotEdges',
        crop: false,
        overflow: 'allow',
        formatter: function () {},
        style: {
          fontSize: '14px',
          fontWeight: 'normal',
          textOutline: 'none'
        }
      }
    }
  },
  series: []
};

const barConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'bar'
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    useHTML: false,
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      fontWeight: 'normal'
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  yAxis: {
    title: {
      text: ''
    },
    labels: {
      format: '{value}%'
    },
    reversedStacks: false
  },
  plotOptions: {
    bar: {
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: true
      }
    },
    series: {
      stacking: 'percent'
    }
  },
  series: []
};

const DEFAULT_POS = { x: 95, y: 85 };

export class CTBreakdown extends React.Component {
  static defaultProps = {
    data: []
  };

  constructor(props) {
    super(props);

    this.state = {
      isPieChart: true,
      isFullSreen: false,
      isLabelsVisible: true,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };
    barConfig.exporting = { ...barConfig.exporting, ...this.props.chartExporting };

    this.initChart();
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.updateFontSize();

    if (this.props.data.length) {
      this.props.getBreakdownData(this.props.data);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.props.getBreakdownData(this.props.data);
    }

    if (prevProps.breakdownData != this.props.breakdownData) {
      this.updateChartData();
    }

    if (prevProps.breakdownType != this.props.breakdownType) {
      this.updateChartType();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChartType() {
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.chart.destroy();

    this.initChart();
    this.updateFontSize();
    this.updateChartData();
  }

  initChart() {
    const config = this.props.breakdownType === 'pie' ? pieConfig : barConfig;
    const that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
        
    config.plotOptions[this.props.breakdownType].dataLabels.formatter =
      this.props.breakdownType === 'pie'
        ? function() {
            const name = escapeHTML(this.key || this.point.name);
            // Handle both count and time values based on timeUnit
            let value;
            if (that.props.timeUnit === 'cards') {
              value = `${this.y} ${this.y === 1 ? 'item' : 'items'}`;
              const timeVal = this.point.timeValue || this.timeValue;
              if (timeVal) {
                const timeStr = formatWorkDuration(timeVal, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                value += ` (${timeStr})`;
              }
            } else {
              value = formatWorkDuration(this.y, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
              const countVal = this.point.countValue || this.countValue;
              if (countVal) {
                value += ` (${countVal} ${countVal === 1 ? 'item' : 'items'})`;
              }
            }
            return `<b>${name}:</b><br/>${value}`;
          }
        : function() {
            return that.dataLabel(that, this);
          };

    this.chart = new Highcharts['Chart'](`breakdown_chart_container`, config);
  }

  updateChartData() {
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (this.state.isPieChart) {
      // For pie chart, map the data to include custom properties in each point
      const pieData = this.props.breakdownData.map(item => ({
        name: item.name,
        y: item.y,
        color: item.color,
        countValue: item.countValue,
        timeValue: item.timeValue,
      }));

      this.chart.addSeries(
        {
          legendType: 'point',
          showInLegend: true,
          colorByPoint: true,
          data: pieData
        },
        true
      );
    } else {
      for (let i = 0; i < this.props.breakdownData.length; i++) {
        const data = {
          ...this.props.breakdownData[i],
          data: [{
            y: this.props.breakdownData[i].y,
            countValue: this.props.breakdownData[i].countValue,
            timeValue: this.props.breakdownData[i].timeValue
          }]
        };
        this.chart.addSeries(data, false);
      }
      this.chart.redraw();
    }
  }

  updateFontSize = () => {
    if (!this.chart) return;

    const isFullscreen = this.state.isFullSreen;
    const isLabelsVisible = this.state.isLabelsVisible;
    const that = this;

    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Breakdown',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },
          legend: {
            itemStyle: {
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '7px',
              fontFamily: 'Poppins'
            }
          },
          plotOptions: {
            pie: {
              dataLabels: {
                useHTML: false,
                formatter() {
                  const name = escapeHTML(this.key || this.point.name);
                  const percentage = this.percentage.toFixed(2);
                  let value;
                  if (that.props.timeUnit === 'cards') {
                    value = `${this.y} ${this.y === 1 ? 'item' : 'items'}`;
                    const timeVal = this.point.timeValue || this.timeValue;
                    if (timeVal) {
                      const timeStr = formatWorkDuration(timeVal, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                      value += ` (${timeStr})`;
                    }
                  } else {
                    value = formatWorkDuration(this.y, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                    const countVal = this.point.countValue || this.countValue;
                    if (countVal) {
                      value += ` (${countVal} ${countVal === 1 ? 'item' : 'items'})`;
                    }
                  }
                  return `<b>${name}</b><br/><b>(${percentage}%)</b><br/>${value}`;
                },
                style: {
                  color: 'rgba(0, 0, 0, 0.87)',
                  fontSize: '6px',
                  fontFamily: 'Poppins',
                }
              }
            },
            bar: {
              dataLabels: {
                style: {
                  textOutline: 'none'
                }
              }
            }
          }
        }
      },
      plotOptions: {
        pie: {
          size: isFullscreen ? '60%' : '75%',
          dataLabels: isFullscreen ? {
            enabled: true,
            useHTML: true,
            connectorWidth: 2,
            //alignTo: 'plotEdges',
            distance: 20,
            crop: false,
            overflow: 'justify',
            style: {
              fontSize: '16px',
              fontWeight: 'normal',
              textOutline: 'none',
              whiteSpace: 'normal',
            },
            formatter: function () {
              const name = escapeHTML(this.key || this.point.name);
              const percentage = this.percentage.toFixed(2);
              // Handle both count and time values based on timeUnit
              let value;
              if (that.props.timeUnit === 'cards') {
                value = `${this.y} ${this.y === 1 ? 'item' : 'items'}`;
                const timeVal = this.point.timeValue || this.timeValue;
                if (timeVal) {
                  const timeStr = formatWorkDuration(timeVal, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                  value += ` (${timeStr})`;
                }
              } else {
                value = formatWorkDuration(this.y, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                const countVal = this.point.countValue || this.countValue;
                if (countVal) {
                  value += ` (${countVal} ${countVal === 1 ? 'item' : 'items'})`;
                }
              }
              return `<div class="tooltip-label-item tooltip-label-box" style="color: #001A33"><strong>${name} (${percentage}%)</strong>${value}</div>`;
            }
          } : {
            enabled: false,
            style: {
              fontSize: '11px'
            }
          }
        },
      },
      legend: {
        enabled: isLabelsVisible,
      }
    });
    this.chart.reflow();
  };

  handleSwitch = () => {
    this.setState({ isPieChart: !this.state.isPieChart }, () => {
      this.props.setChartType(this.state.isPieChart ? 'pie' : 'bar');
      this.props.saveConfig();
    });
  };
  
  handleHideLabels = () => {
    this.setState({ isLabelsVisible: !this.state.isLabelsVisible }, () => this.updateFontSize());      
  }

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  tooltipFormatter(comp, ev) {
    const name = escapeHTML(comp.props.breakdownType === 'pie' ? ev.key : ev.series.name);
    const point = ev.point || ev;
    const count = point.countValue !== undefined ? point.countValue : ev.y;
    const countValue = `${count} ${count === 1 ? 'work item' : 'work items'}`;
    const time = point.timeValue !== undefined ? point.timeValue : ev.y;
    const timeValue = formatWorkDuration(time, comp.props.excludeWeekendOpts, comp.props.timePrecision, comp.props.useWorkingHoursInCycleTimeDays);    
    
    return `
      <table style="margin: 0; padding: 0; border-spacing: 0;">
        <tr>
          <td colspan="2" style="padding: 0 0 4px 0;">
            <span style="background-color: ${ev.color}; display: inline-block; padding: 0px; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px;"></span>
            <strong>${name}</strong>
          </td>
        </tr>
        <tr><td style="padding: 0; width: 40px;">Items:</td><td style="padding: 0 0 0 2px;">${countValue}</td></tr>
        <tr><td style="padding: 0; width: 40px;">Time:</td><td style="padding: 0 0 0 2px;">${timeValue}</td></tr>
        <tr><td style="padding: 0; width: 40px;">Share:</td><td style="padding: 0 0 0 2px;">${ev.percentage.toFixed(2)}%</td></tr>
      </table>
    `;
  }

  dataLabel(comp, ev) {
    return `${ev.percentage.toFixed(2)}%`;
  }

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box breakdown ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className="box_white_title">
            <h3>Breakdown</h3>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id="labelsTooltip" className="tooltip_dark">
                  {this.state.isLabelsVisible ? 'Hide labels' : 'Show labels'}
                </Tooltip>
              }>
              <div className="labels-toggle" onClick={this.handleHideLabels}>
                <img
                  src={arrowDownIcon}
                  alt="toggle labels"
                  style={{ transform: this.state.isLabelsVisible ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </div>
            </OverlayTrigger>
            <div className='right-actions'>
              <Button variant='secondary' size='sm' className='btn-action' onClick={this.handleSwitch}>
                <img src={this.state.isPieChart ? barIcon : pieIcon} alt="type" />
              </Button>
              <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <div id="breakdown_chart_container" className={'no-stroke'} />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getBreakdownData: data => dispatch(getBreakdownData(data)),
    close: () => dispatch(updateBreakdown(false, 'ctb')),
    setChartType: type => dispatch(updateBreakdownType(type, 'ctb')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    breakdownData: state.ctb.breakdown,
    timePrecision: state.filters.timePrecision,
    breakdownType: state.filters.ctb.breakdownType,
    timeUnit: state.filters.ctb.timeUnit,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(CTBreakdown));
